import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { CopyButton } from "./CopyButton";

export const TRUNCATE = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const CODE_FONT = {
  fontFamily: "'Roboto Mono', ui-monospace, monospace",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "2px",
};

export function groupDigits(code: string): string {
  return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

export function TotpCode({
  fetchCode,
}: {
  fetchCode: () => Promise<TotpMessage>;
}) {
  const [displayValue, setDisplayValue] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [period, setPeriod] = useState(30);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const loadCode = async () => {
    try {
      const totp = await fetchCode();
      setCode(totp.code);
      setSecondsLeft(totp.expires_in);
      setPeriod(totp.period || 30);
      setError(null);
    } catch {
      setError("Failed to load code");
      setCode(null);
      setSecondsLeft(null);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding clearTimer would change when this runs
  useEffect(() => {
    if (displayValue) {
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
      }, 1000);
    }
    return clearTimer;
  }, [displayValue]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding loadCode would change when this runs
  useEffect(() => {
    if (displayValue && secondsLeft === 0) {
      loadCode();
    }
  }, [displayValue, secondsLeft]);

  const reveal = async () => {
    await loadCode();
    setDisplayValue(true);
  };

  const hide = () => {
    setDisplayValue(false);
    clearTimer();
    setCode(null);
    setSecondsLeft(null);
    setError(null);
  };

  const expiring = secondsLeft !== null && secondsLeft <= 5;

  let content: ReactNode;
  if (!displayValue) {
    content = (
      <Box sx={{ ...CODE_FONT, ...TRUNCATE, color: "#98A2B3" }}>••• •••</Box>
    );
  } else if (error) {
    content = <Box sx={{ ...TRUNCATE, color: "#B42318" }}>{error}</Box>;
  } else {
    content = (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
      >
        <Box
          data-sr-redact
          sx={{
            ...CODE_FONT,
            ...TRUNCATE,
            color: expiring ? "#B42318" : "#101828",
          }}
        >
          {code ? groupDigits(code) : "…"}
        </Box>
        {secondsLeft !== null && (
          <Box
            sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
          >
            <CircularProgress
              variant="determinate"
              value={100}
              size={18}
              thickness={5}
              sx={{ color: "#EAECF0", position: "absolute" }}
            />
            <CircularProgress
              variant="determinate"
              value={(secondsLeft / period) * 100}
              size={18}
              thickness={5}
              sx={{ color: expiring ? "#B42318" : "#475467" }}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
      }}
    >
      {content}
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {displayValue && code && <CopyButton getText={() => code} />}
        <IconButton
          aria-label="toggle totp"
          size="small"
          sx={{ color: "#475467", padding: "5px" }}
          onClick={async () => {
            if (displayValue) {
              hide();
            } else {
              await reveal();
            }
          }}
        >
          {displayValue ? (
            <VisibilityOffOutlinedIcon sx={{ width: "20px", height: "20px" }} />
          ) : (
            <VisibilityOutlinedIcon sx={{ width: "20px", height: "20px" }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}
